"use client";

import { useState, useRef, useCallback, useEffect, type MutableRefObject } from "react";
import * as Sentry from "@sentry/react";
import { useAppStore } from "@/stores";
import { safeRandomUUID } from "@/lib/utils/uuid";
import type { RecordingMode, SavedTake, TakeAutosave } from "../types";
import {
  allowTakeSaving,
  deleteTake,
  hasSpaceForTake,
  holdTakeLock,
  isTakeStoreAvailable,
  loadTakeBlob,
  loadTakeChunks,
  requestPersistentStorage,
  saveTake,
} from "../take-store";

export interface MediaDevice {
  deviceId: string;
  label: string;
  kind: MediaDeviceKind;
}

export interface RecordingEngineState {
  isRecording: boolean;
  isPaused: boolean;
  isReadyToSubmit: boolean;
  elapsedTime: number;
  blob: Blob | null;
  previewUrl: string | null;
  error: string | null;
  audioOutputDevices: MediaDevice[];
  selectedOutputDevice: string;
  microphoneActive: boolean;
  screenShareAudioTracks: number;
  screenShareVideoTracks: number;
  cameraReadyState: MediaStreamTrackState;
  microphoneReadyState: MediaStreamTrackState;
  cameraPosition: "user" | "environment";
  autosave: TakeAutosave;
}

/** Shape of the wavesurfer microphone plugin that the engine holds a ref to. */
export interface WavesurferMicPlugin {
  start: () => void;
  pause: () => void;
  play: () => void;
  stop: () => void;
}

export interface RecordingEngineReturn extends RecordingEngineState {
  // Device state
  audioDevices: MediaDevice[];
  videoDevices: MediaDevice[];
  selectedAudioDevice: string;
  selectedVideoDevice: string;
  setSelectedAudioDevice: (id: string) => void;
  setSelectedVideoDevice: (id: string) => void;
  refreshDevices: () => Promise<void>;

  setSelectedOutputDevice: (id: string) => void;

  // No-ops on browsers that lack setSinkId (Firefox < 116, iOS Safari).
  setSinkIdOnElement: (el: HTMLMediaElement | null, deviceId: string) => Promise<void>;

  setScreenShareTrackCounts: (counts: { audio: number; video: number }) => void;

  setCameraReadyState: (state: MediaStreamTrackState) => void;
  setMicrophoneReadyState: (state: MediaStreamTrackState) => void;

  switchCameraPosition: () => void;

  wavesurferMicrophoneRef: MutableRefObject<WavesurferMicPlugin | null>;
  attachWavesurferMicrophone: (plugin: WavesurferMicPlugin) => void;
  detachWavesurferMicrophone: () => void;

  startMicHealthMonitor: (
    stream: MediaStream,
    opts?: { videoTracks?: boolean; secondaryStream?: MediaStream | null },
  ) => void;
  stopMicHealthMonitor: () => void;

  attachTrackInactivityWatcher: (
    stream: MediaStream,
    onInactive: () => void,
  ) => () => void;

  stream: MediaStream | null;

  startTimer: () => void;
  stopTimer: () => void;

  pauseRecording: () => void;
  resumeRecording: () => void;

  discardRecording: () => void;

  // Same path as discardRecording; explicit name for the submit handler.
  clearRecordingAfterUpload: () => void;

  setActiveStream: (stream: MediaStream | null) => void;
  setError: (message: string | null) => void;

  recorderRef: React.MutableRefObject<MediaRecorder | null>;
  streamRef: React.MutableRefObject<MediaStream | null>;
  chunksRef: React.MutableRefObject<Blob[]>;
  // Mirrors state.previewUrl, cleanup effects read refs, not stale state.
  previewUrlRef: React.MutableRefObject<string | null>;

  /**
   * Called by per-mode hooks inside recorder.onstop to commit the final blob
   * and previewUrl into engine state and set isReadyToSubmit = true.
   */
  finalizeRecording: (blob: Blob, previewUrl: string) => void;

  /** Starts saving the new take's chunks in this browser (audio only for now); call right after recorder.start. */
  startTake: (mode: RecordingMode, mimeType: string) => void;
  /** Called from recorder.onstop: builds the file from the saved and unsaved chunks, then finalizes it. */
  completeRecording: (mimeType: string) => Promise<void>;
  /** Loads a saved take into the ready-to-upload state; rejects when it cannot be read. */
  restoreTake: (take: SavedTake) => Promise<void>;
}

export interface UseRecordingEngineOptions {
  /** Owner of saved takes; without it nothing is saved in the browser. */
  userId?: string;
}

/** Maximum recording duration in seconds (4 hours). Exported so per-mode hooks can reference it without redeclaring. */
export const MAX_RECORDING_SECONDS = 14400;

const TAKE_FLUSH_INTERVAL_MS = 5000;
// Video and screen write about 20 MB a minute; they stay memory-only until their browser save is tested.
const AUTOSAVE_MODES: readonly RecordingMode[] = ["audio"];

const QUALITY_CONSTRAINTS: Record<string, { width: number; height: number }> = {
  "480p": { width: 854, height: 480 },
  "720p": { width: 1280, height: 720 },
  "1080p": { width: 1920, height: 1080 },
};

export { QUALITY_CONSTRAINTS };

/**
 * Poll every 50ms until getSocketState() returns true, then resolve.
 * Rejects if the socket has not become ready within timeoutMs (default 5000ms).
 */
export function awaitSocketReady(
  getSocketState: () => boolean,
  timeoutMs = 5000,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const start = Date.now();
    const interval = setInterval(() => {
      if (getSocketState()) {
        clearInterval(interval);
        resolve();
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        clearInterval(interval);
        reject(new Error(`Socket not initialized within ${timeoutMs}ms`));
      }
    }, 50);
  });
}

const MIC_HEALTH_INTERVAL_MS = 3000;

export function useRecordingEngine({ userId }: UseRecordingEngineOptions = {}): RecordingEngineReturn {
  const [state, setState] = useState<RecordingEngineState>({
    isRecording: false,
    isPaused: false,
    isReadyToSubmit: false,
    elapsedTime: 0,
    blob: null,
    previewUrl: null,
    error: null,
    audioOutputDevices: [],
    selectedOutputDevice: "default",
    microphoneActive: true,
    screenShareAudioTracks: 0,
    screenShareVideoTracks: 0,
    cameraReadyState: "live",
    microphoneReadyState: "live",
    cameraPosition: "user",
    autosave: "off",
  });

  const [audioDevices, setAudioDevices] = useState<MediaDevice[]>([]);
  const [videoDevices, setVideoDevices] = useState<MediaDevice[]>([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>("");
  const [selectedVideoDevice, setSelectedVideoDevice] = useState<string>("");
  const [stream, setStream] = useState<MediaStream | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const pausedTimeRef = useRef<number>(0);
  // Mirrors state.previewUrl, cleanup effects close over refs (stable), not state (stale).
  const previewUrlRef = useRef<string | null>(null);

  const wavesurferMicrophoneRef = useRef<WavesurferMicPlugin | null>(null);

  const micHealthIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Tracks last-known mic-active state so the health monitor only reacts on transitions.
  const microphoneActiveRef = useRef<boolean>(true);

  // isRecording / isPaused refs for use inside intervals, avoid stale state closures.
  const isRecordingRef = useRef(false);
  const isPausedRef = useRef(false);

  useEffect(() => {
    isRecordingRef.current = state.isRecording;
    isPausedRef.current = state.isPaused;
  }, [state.isRecording, state.isPaused]);

  const audioOutputDevicesRef = useRef<MediaDevice[]>([]);

  const userIdRef = useRef(userId);
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);
  const takeRef = useRef<SavedTake | null>(null);
  const autosaveRef = useRef<TakeAutosave>("off");
  const savedChunkCountRef = useRef(0);
  const takeWritesRef = useRef<Promise<void>>(Promise.resolve());
  const takeFlushIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const releaseTakeLockRef = useRef<(() => void) | null>(null);
  const elapsedRef = useRef(0);
  const unmountedRef = useRef(false);

  const setRecordingStatus = useAppStore((s) => s.setRecordingStatus);
  const setStoreElapsedTime = useAppStore((s) => s.setElapsedTime);
  const setIsMicActive = useAppStore((s) => s.setIsMicActive);

  useEffect(() => {
    refreshDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();

      const audio = devices
        .filter((d) => d.kind === "audioinput")
        .map((d) => ({
          deviceId: d.deviceId,
          label: d.label || `Microphone ${d.deviceId.slice(0, 5)}`,
          kind: d.kind,
        }));

      const video = devices
        .filter((d) => d.kind === "videoinput")
        .map((d) => ({
          deviceId: d.deviceId,
          label: d.label || `Camera ${d.deviceId.slice(0, 5)}`,
          kind: d.kind,
        }));

      const audioOutput = devices
        .filter((d) => d.kind === "audiooutput")
        .map((d) => ({
          deviceId: d.deviceId,
          label: d.label || `Speaker ${d.deviceId.slice(0, 5)}`,
          kind: d.kind,
        }));

      setAudioDevices(audio);
      setVideoDevices(video);
      audioOutputDevicesRef.current = audioOutput;

      setState((prev) => {
        const nextOutput =
          !prev.selectedOutputDevice || prev.selectedOutputDevice === "default"
            ? audioOutput.length > 0
              ? audioOutput[0].deviceId
              : "default"
            : prev.selectedOutputDevice;
        return {
          ...prev,
          audioOutputDevices: audioOutput,
          selectedOutputDevice: nextOutput,
        };
      });

      if (audio.length > 0 && !selectedAudioDevice) {
        setSelectedAudioDevice(audio[0].deviceId);
      }
      if (video.length > 0 && !selectedVideoDevice) {
        setSelectedVideoDevice(video[0].deviceId);
      }
    } catch {
      setState((prev) => ({
        ...prev,
        error: "Failed to enumerate media devices. Check permissions.",
      }));
    }
  }, [selectedAudioDevice, selectedVideoDevice]);

  const setSelectedOutputDevice = useCallback((id: string) => {
    setState((prev) => ({ ...prev, selectedOutputDevice: id }));
  }, []);

  const setSinkIdOnElement = useCallback(
    async (el: HTMLMediaElement | null, deviceId: string): Promise<void> => {
      if (
        !el ||
        !("sinkId" in HTMLMediaElement.prototype) ||
        typeof (el as HTMLMediaElement & { setSinkId?: (id: string) => Promise<void> }).setSinkId !==
          "function"
      ) {
        return;
      }
      try {
        await (el as HTMLMediaElement & { setSinkId: (id: string) => Promise<void> }).setSinkId(
          deviceId,
        );
      } catch (err) {
        if (err instanceof DOMException && err.name === "SecurityError") {
          setState((prev) => ({
            ...prev,
            error: "HTTPS required for selecting audio output device",
            selectedOutputDevice:
              audioOutputDevicesRef.current.length > 0
                ? audioOutputDevicesRef.current[0].deviceId
                : "default",
          }));
        } else if (err instanceof Error) {
          setState((prev) => ({ ...prev, error: "Could not switch audio output. Please try again." }));
        }
      }
    },
    [],
  );

  const setScreenShareTrackCounts = useCallback(
    (counts: { audio: number; video: number }) => {
      setState((prev) => ({
        ...prev,
        screenShareAudioTracks: counts.audio,
        screenShareVideoTracks: counts.video,
      }));
    },
    [],
  );

  const setCameraReadyState = useCallback((readyState: MediaStreamTrackState) => {
    setState((prev) => ({ ...prev, cameraReadyState: readyState }));
  }, []);

  const setMicrophoneReadyState = useCallback((readyState: MediaStreamTrackState) => {
    setState((prev) => ({ ...prev, microphoneReadyState: readyState }));
  }, []);

  const switchCameraPosition = useCallback(() => {
    setState((prev) => ({
      ...prev,
      cameraPosition: prev.cameraPosition === "user" ? "environment" : "user",
    }));
  }, []);

  const attachWavesurferMicrophone = useCallback((plugin: WavesurferMicPlugin) => {
    wavesurferMicrophoneRef.current = plugin;
  }, []);

  const detachWavesurferMicrophone = useCallback(() => {
    if (wavesurferMicrophoneRef.current) {
      wavesurferMicrophoneRef.current.stop();
      wavesurferMicrophoneRef.current = null;
    }
  }, []);

  const stopMicHealthMonitor = useCallback(() => {
    if (micHealthIntervalRef.current) {
      clearInterval(micHealthIntervalRef.current);
      micHealthIntervalRef.current = null;
    }
  }, []);

  const startMicHealthMonitor = useCallback(
    (
      stream: MediaStream,
      opts?: { videoTracks?: boolean; secondaryStream?: MediaStream | null },
    ) => {
      stopMicHealthMonitor();
      microphoneActiveRef.current = true;

      micHealthIntervalRef.current = setInterval(() => {
        if (isPausedRef.current || !isRecordingRef.current) return;

        const secondaryAudio = opts?.secondaryStream?.getAudioTracks() ?? [];
        const audioTracks = [...stream.getAudioTracks(), ...secondaryAudio];

        const hasActiveAudio = audioTracks.some(
          (t) => t.readyState === "live" && !t.muted,
        );

        if (!hasActiveAudio) {
          if (microphoneActiveRef.current) {
            microphoneActiveRef.current = false;
            setState((prev) => ({
              ...prev,
              microphoneReadyState: "ended",
              microphoneActive: false,
              error: opts?.videoTracks
                ? "Microphone disconnected, recording paused. Reconnect and tap Resume."
                : opts?.secondaryStream !== undefined
                ? "Screen share audio was muted or disconnected. Please check your system audio settings."
                : "Microphone disconnected, recording paused. Reconnect and tap Resume.",
            }));
          }
          // Pause only on the first inactive detection (guard prevents double-pause)
          if (recorderRef.current && recorderRef.current.state === "recording") {
            pausedTimeRef.current = Date.now() - startTimeRef.current;
            recorderRef.current.pause();
            setState((prev) => ({ ...prev, isPaused: true }));
            setRecordingStatus("paused");
            if (timerRef.current) {
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
          }
        } else if (!microphoneActiveRef.current) {
          microphoneActiveRef.current = true;
          setState((prev) => ({
            ...prev,
            microphoneReadyState: "live",
            microphoneActive: true,
            error: null,
          }));
        }

        if (opts?.videoTracks) {
          const secondaryVideo = opts?.secondaryStream?.getVideoTracks() ?? [];
          const videoTracks = [...stream.getVideoTracks(), ...secondaryVideo];
          const hasActiveVideo = videoTracks.some(
            (t) => t.readyState === "live" && !t.muted,
          );

          if (!hasActiveVideo && videoTracks.length > 0) {
            setState((prev) => {
              if (prev.cameraReadyState === "ended") return prev;
              return {
                ...prev,
                cameraReadyState: "ended",
                error: "Camera was disconnected. Please check your camera settings.",
              };
            });
            if (recorderRef.current && recorderRef.current.state === "recording") {
              pausedTimeRef.current = Date.now() - startTimeRef.current;
              recorderRef.current.pause();
              setState((prev) => ({ ...prev, isPaused: true }));
              setRecordingStatus("paused");
              if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
              }
            }
          } else if (hasActiveVideo) {
            setState((prev) => {
              if (prev.cameraReadyState === "live") return prev;
              return { ...prev, cameraReadyState: "live" };
            });
          }
        }
      }, MIC_HEALTH_INTERVAL_MS);
    },
    [stopMicHealthMonitor, setRecordingStatus],
  );

  /**
   * Subscribe to track ended/mute events on a stream. Calls onInactive when all audio
   * tracks become inactive (ended or muted with no recovery within 2s). Returns a
   * cleanup function. Screen-mode callers skip this, transient mute on screen audio is expected.
   */
  const attachTrackInactivityWatcher = useCallback(
    (stream: MediaStream, onInactive: () => void): (() => void) => {
      const muteTimerRefs = new Map<string, ReturnType<typeof setTimeout>>();

      const checkAllInactive = () => {
        const audio = stream.getAudioTracks();
        if (audio.length === 0) return;
        const allInactive = audio.every(
          (t) => t.readyState === "ended" || t.muted,
        );
        if (allInactive) {
          onInactive();
        }
      };

      const handlers: Array<{ track: MediaStreamTrack; type: string; fn: EventListener }> = [];

      for (const track of stream.getTracks()) {
        const onEnded: EventListener = () => {
          checkAllInactive();
        };
        const onMute: EventListener = () => {
          const timer = setTimeout(checkAllInactive, 2000);
          muteTimerRefs.set(track.id, timer);
        };
        const onUnmute: EventListener = () => {
          const existing = muteTimerRefs.get(track.id);
          if (existing !== undefined) {
            clearTimeout(existing);
            muteTimerRefs.delete(track.id);
          }
        };

        track.addEventListener("ended", onEnded);
        track.addEventListener("mute", onMute);
        track.addEventListener("unmute", onUnmute);

        handlers.push({ track, type: "ended", fn: onEnded });
        handlers.push({ track, type: "mute", fn: onMute });
        handlers.push({ track, type: "unmute", fn: onUnmute });
      }

      return () => {
        for (const { track, type, fn } of handlers) {
          track.removeEventListener(type, fn);
        }
        muteTimerRefs.forEach((t) => clearTimeout(t));
        muteTimerRefs.clear();
      };
    },
    [],
  );

  /**
   * Start the recording timer. Also the canonical auto-stop trigger: when elapsed >=
   * MAX_RECORDING_SECONDS the interval calls recorderRef.current.stop(). Per-mode hooks
   * MUST NOT add a redundant setTimeout, double-stop throws InvalidStateError.
   */
  const startTimer = useCallback(() => {
    startTimeRef.current = Date.now() - pausedTimeRef.current;

    setState((prev) => ({ ...prev, isRecording: true, error: null }));
    setRecordingStatus("recording");

    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      elapsedRef.current = elapsed;
      setState((prev) => ({ ...prev, elapsedTime: elapsed }));
      setStoreElapsedTime(elapsed);

      if (elapsed >= MAX_RECORDING_SECONDS) {
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
          recorderRef.current.stop();
        }
      }
    }, 1000);
  }, [setRecordingStatus, setStoreElapsedTime]);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      elapsedRef.current = elapsed;
      setState((prev) => ({ ...prev, elapsedTime: elapsed }));
      setStoreElapsedTime(elapsed);
    }
  }, [setStoreElapsedTime]);

  const pauseRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state === "recording") {
      recorderRef.current.pause();
      // Wall-clock math avoids stale state.elapsedTime closure.
      pausedTimeRef.current = Date.now() - startTimeRef.current;
      stopTimer();

      setState((prev) => ({ ...prev, isPaused: true }));
      setRecordingStatus("paused");
    }
  }, [stopTimer, setRecordingStatus]);

  const resumeRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state === "paused") {
      recorderRef.current.resume();
      startTimer();

      setState((prev) => ({ ...prev, isPaused: false }));
      setRecordingStatus("recording");
    }
  }, [startTimer, setRecordingStatus]);

  const finalizeRecording = useCallback((blob: Blob, previewUrl: string) => {
    setState((prev) => {
      // discardRecording resets isRecording → false synchronously before the async onstop fires.
      // If we hit that path, revoke the URL and leave state alone, discard's reset is correct.
      if (!prev.isRecording) {
        URL.revokeObjectURL(previewUrl);
        return prev;
      }
      previewUrlRef.current = previewUrl;
      return {
        ...prev,
        isRecording: false,
        isReadyToSubmit: true,
        blob,
        previewUrl,
      };
    });
  }, []);

  const setAutosave = useCallback((autosave: TakeAutosave) => {
    autosaveRef.current = autosave;
    setState((prev) => (prev.autosave === autosave ? prev : { ...prev, autosave }));
  }, []);

  // Every IndexedDB write for a take runs in this one chain so a batch, the ready mark and a delete never overlap or reorder.
  const queueTakeWrite = useCallback((write: () => Promise<void>): Promise<void> => {
    const next = takeWritesRef.current.then(write);
    takeWritesRef.current = next.catch(() => undefined);
    return next;
  }, []);

  const failAutosave = useCallback(
    (err: unknown) => {
      if (!(err instanceof DOMException && err.name === "QuotaExceededError")) {
        Sentry.captureException(err, { tags: { feature: "recorder" } });
      }
      setAutosave("failed");
    },
    [setAutosave],
  );

  const releaseTakeLock = useCallback(() => {
    releaseTakeLockRef.current?.();
    releaseTakeLockRef.current = null;
  }, []);

  const stopTakeFlushTimer = useCallback(() => {
    if (takeFlushIntervalRef.current) {
      clearInterval(takeFlushIntervalRef.current);
      takeFlushIntervalRef.current = null;
    }
  }, []);

  const flushTakeChunks = useCallback(
    () =>
      queueTakeWrite(async () => {
        const take = takeRef.current;
        if (!take || autosaveRef.current !== "on") return;
        const batch = chunksRef.current.slice();
        if (batch.length === 0 && savedChunkCountRef.current === 0) return;
        const updated: SavedTake = { ...take, durationSec: elapsedRef.current, updatedAt: Date.now() };
        try {
          await saveTake(updated, batch, savedChunkCountRef.current);
        } catch (err) {
          if (takeRef.current?.id === take.id) failAutosave(err);
          return;
        }
        if (takeRef.current?.id !== take.id) return;
        takeRef.current = updated;
        chunksRef.current.splice(0, batch.length);
        savedChunkCountRef.current += batch.length;
      }),
    [queueTakeWrite, failAutosave],
  );

  const startTake = useCallback(
    (mode: RecordingMode, mimeType: string) => {
      releaseTakeLock();
      stopTakeFlushTimer();
      takeRef.current = null;
      savedChunkCountRef.current = 0;
      elapsedRef.current = 0;
      setAutosave("off");
      const owner = userIdRef.current;
      if (!owner || !isTakeStoreAvailable() || !AUTOSAVE_MODES.includes(mode)) return;
      allowTakeSaving(owner);

      const now = Date.now();
      const take: SavedTake = {
        id: safeRandomUUID(),
        userId: owner,
        mode,
        mimeType,
        status: "recording",
        startedAt: now,
        updatedAt: now,
        durationSec: 0,
      };
      takeRef.current = take;
      releaseTakeLockRef.current = holdTakeLock(take.id);
      requestPersistentStorage();
      void queueTakeWrite(async () => {
        const hasSpace = await hasSpaceForTake(mode);
        if (takeRef.current?.id !== take.id) return;
        setAutosave(hasSpace ? "on" : "failed");
      });
      takeFlushIntervalRef.current = setInterval(() => {
        void flushTakeChunks();
      }, TAKE_FLUSH_INTERVAL_MS);
    },
    [releaseTakeLock, stopTakeFlushTimer, setAutosave, queueTakeWrite, flushTakeChunks],
  );

  const completeRecording = useCallback(
    async (mimeType: string) => {
      stopTakeFlushTimer();
      const take = takeRef.current;
      let savedChunks: Blob[] = [];
      if (take) {
        await flushTakeChunks();
        try {
          await queueTakeWrite(async () => {
            if (savedChunkCountRef.current === 0 || takeRef.current?.id !== take.id) return;
            savedChunks = await loadTakeChunks(take.id);
            if (autosaveRef.current !== "on") return;
            const ready: SavedTake = { ...takeRef.current, status: "ready", durationSec: elapsedRef.current, updatedAt: Date.now() };
            try {
              await saveTake(ready);
              takeRef.current = ready;
            } catch (err) {
              failAutosave(err);
            }
          });
        } catch (err) {
          Sentry.captureException(err, { tags: { feature: "recorder" } });
          releaseTakeLock();
          setState((prev) => ({
            ...prev,
            isRecording: false,
            error: "Your recording is saved in this browser but could not be opened. Reload this page to recover it.",
          }));
          return;
        }
      }
      if (unmountedRef.current) {
        releaseTakeLock();
        return;
      }
      const parts = [...savedChunks, ...chunksRef.current];
      chunksRef.current = [];
      const blob = new Blob(parts, { type: mimeType || parts[0]?.type || take?.mimeType || "" });
      finalizeRecording(blob, URL.createObjectURL(blob));
    },
    [stopTakeFlushTimer, flushTakeChunks, queueTakeWrite, failAutosave, releaseTakeLock, finalizeRecording],
  );

  const restoreTake = useCallback(
    async (take: SavedTake) => {
      const blob = await loadTakeBlob(take);
      releaseTakeLock();
      stopTakeFlushTimer();
      takeRef.current = { ...take, status: "ready" };
      chunksRef.current = [];
      savedChunkCountRef.current = 0;
      elapsedRef.current = take.durationSec;
      releaseTakeLockRef.current = holdTakeLock(take.id);
      setAutosave("on");
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      const url = URL.createObjectURL(blob);
      previewUrlRef.current = url;
      setState((prev) => ({
        ...prev,
        isRecording: false,
        isPaused: false,
        isReadyToSubmit: true,
        elapsedTime: take.durationSec,
        blob,
        previewUrl: url,
        error: null,
      }));
    },
    [releaseTakeLock, stopTakeFlushTimer, setAutosave],
  );

  const discardRecording = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }

    const take = takeRef.current;
    takeRef.current = null;
    savedChunkCountRef.current = 0;
    autosaveRef.current = "off";
    stopTakeFlushTimer();
    releaseTakeLock();
    if (take) {
      void queueTakeWrite(() => deleteTake(take.id)).catch((err) =>
        Sentry.captureException(err, { tags: { feature: "recorder" } }),
      );
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setStream(null);
    }

    // Read from ref, state.previewUrl is stale in this closure.
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }

    chunksRef.current = [];
    pausedTimeRef.current = 0;
    stopTimer();
    stopMicHealthMonitor();

    setState((prev) => ({
      isRecording: false,
      isPaused: false,
      isReadyToSubmit: false,
      elapsedTime: 0,
      blob: null,
      previewUrl: null,
      error: null,
      audioOutputDevices: prev.audioOutputDevices,
      selectedOutputDevice: prev.selectedOutputDevice,
      microphoneActive: true,
      screenShareAudioTracks: 0,
      screenShareVideoTracks: 0,
      cameraReadyState: "live",
      microphoneReadyState: "live",
      cameraPosition: prev.cameraPosition,
      autosave: "off",
    }));

    setRecordingStatus("idle");
    setIsMicActive(false);
    setStoreElapsedTime(0);
  }, [
    stopTimer,
    stopMicHealthMonitor,
    setRecordingStatus,
    setIsMicActive,
    setStoreElapsedTime,
    stopTakeFlushTimer,
    releaseTakeLock,
    queueTakeWrite,
  ]);

  const setActiveStream = useCallback((s: MediaStream | null) => {
    streamRef.current = s;
    setStream(s);
  }, []);

  const setEngineError = useCallback((message: string | null) => {
    setState((prev) => ({ ...prev, error: message }));
  }, []);

  const clearRecordingAfterUpload = discardRecording;

  useEffect(() => {
    const flushWhenHidden = () => {
      if (document.visibilityState === "hidden") void flushTakeChunks();
    };
    const flushOnPageHide = () => {
      void flushTakeChunks();
    };
    document.addEventListener("visibilitychange", flushWhenHidden);
    window.addEventListener("pagehide", flushOnPageHide);
    return () => {
      document.removeEventListener("visibilitychange", flushWhenHidden);
      window.removeEventListener("pagehide", flushOnPageHide);
    };
  }, [flushTakeChunks]);

  // Unmount cleanup reads refs, not state, empty dep array is intentional.
  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      if (takeFlushIntervalRef.current) {
        clearInterval(takeFlushIntervalRef.current);
        takeFlushIntervalRef.current = null;
      }
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      } else {
        releaseTakeLockRef.current?.();
        releaseTakeLockRef.current = null;
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (micHealthIntervalRef.current) {
        clearInterval(micHealthIntervalRef.current);
      }
      if (wavesurferMicrophoneRef.current) {
        try {
          wavesurferMicrophoneRef.current.stop();
        } catch {
          // plugin may already be destroyed
        }
        wavesurferMicrophoneRef.current = null;
      }
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, []);

  return {
    ...state,
    audioDevices,
    videoDevices,
    selectedAudioDevice,
    selectedVideoDevice,
    setSelectedAudioDevice,
    setSelectedVideoDevice,
    refreshDevices,
    setSelectedOutputDevice,
    setSinkIdOnElement,
    setScreenShareTrackCounts,
    setCameraReadyState,
    setMicrophoneReadyState,
    switchCameraPosition,
    wavesurferMicrophoneRef,
    attachWavesurferMicrophone,
    detachWavesurferMicrophone,
    startMicHealthMonitor,
    stopMicHealthMonitor,
    attachTrackInactivityWatcher,
    stream,
    startTimer,
    stopTimer,
    pauseRecording,
    resumeRecording,
    discardRecording,
    clearRecordingAfterUpload,
    setActiveStream,
    setError: setEngineError,
    recorderRef,
    streamRef,
    chunksRef,
    previewUrlRef,
    finalizeRecording,
    startTake,
    completeRecording,
    restoreTake,
  };
}
